import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { useOpsBrands } from "@/hooks/useOpsBrands";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Check, Eye, Pencil, Trash2, UserPlus, Download } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

const STATUS_COLORS = { draft: "bg-amber-100 text-amber-800", active: "bg-emerald-100 text-emerald-800" };

const GST_STATE_CODES = {"01":"Jammu & Kashmir","02":"Himachal Pradesh","03":"Punjab","04":"Chandigarh","05":"Uttarakhand","06":"Haryana","07":"Delhi","08":"Rajasthan","09":"Uttar Pradesh","10":"Bihar","11":"Sikkim","12":"Arunachal Pradesh","13":"Nagaland","14":"Manipur","15":"Mizoram","16":"Tripura","17":"Meghalaya","18":"Assam","19":"West Bengal","20":"Jharkhand","21":"Odisha","22":"Chhattisgarh","23":"Madhya Pradesh","24":"Gujarat","26":"Dadra & Nagar Haveli","27":"Maharashtra","28":"Andhra Pradesh","29":"Karnataka","30":"Goa","31":"Lakshadweep","32":"Kerala","33":"Tamil Nadu","34":"Puducherry","35":"Andaman & Nicobar","36":"Telangana","37":"Andhra Pradesh (New)","38":"Ladakh"};

const EMPTY_CONTACT = { name: "", role: "", phones: [""], emails: [""], whatsapp: "" };
const EMPTY_BANK = { account_name: "", account_number: "", ifsc: "", bank_name: "", branch: "", is_primary: true };

const EMPTY_VENDOR = {
  vendor_name: "", vendor_type: "", brand: "Skippi", gstin: "", pan: "",
  address_line1: "", address_line2: "", nearest_landmark: "", city: "", state: "", pincode: "", country: "India",
  contacts: [{ ...EMPTY_CONTACT }],
  supplied_item_categories: [],
  supplied_item_names: [],
  payment_terms_days: 30, credit_limit: 0,
  bank_accounts: [{ ...EMPTY_BANK }],
  pm_logistics_borne_by: "vendor", drop_ship_address: {},
  primary_coordinator_user_id: "", backup_coordinator_user_id: "", is_backup_active: false,
  linked_sku_ids: [], notes: "",
};

function isAdmin(user) {
  return (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
}

export default function OpsVendorsPage() {
  const { getValues } = useConfig();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const canDraft = (user?.permissions || []).some(p => ["vendor.create_draft", "*"].includes(p));
  const vendorTypes = getValues("ops_vendor_types");
  const indianStates = getValues("indian_states");
  const itemCategories = getValues("ops_item_categories");
  const contactRoles = getValues("ops_contact_roles");
  const brands = getValues("ops_brands");
  const { brandLinkOn, brandOptions } = useOpsBrands();   // FX1 C2

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSupplied, setFilterSupplied] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_VENDOR });
  const [editId, setEditId] = useState(null);
  const [viewVendor, setViewVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [expandedDiff, setExpandedDiff] = useState({});

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.vendor_type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterSupplied) params.supplied_item = filterSupplied;
      const { data } = await api.get("/ops/inventory/vendors", { params });
      setVendors(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [search, filterType, filterStatus, filterSupplied]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : data.users || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (admin) api.get("/ops/pending-counts").then(({ data }) => setPendingCount(data.vendor_master || 0)).catch(() => {});
  }, [admin]);

  const openCreate = () => { setEditId(null); setFormData({ ...EMPTY_VENDOR, contacts: [{ ...EMPTY_CONTACT }], bank_accounts: [{ ...EMPTY_BANK }] }); setShowForm(true); };
  const openEdit = (v) => {
    setEditId(v._id);
    // Backward compat: convert legacy flat fields
    let contacts = v.contacts;
    if (!contacts || contacts.length === 0) {
      contacts = [{ name: v.primary_contact_name || "", role: "Owner", phones: [v.primary_contact_phone || ""], emails: [v.primary_contact_email || ""], whatsapp: "" }];
    } else {
      // Ensure each contact has phones and emails arrays
      contacts = contacts.map(c => ({
        ...c,
        phones: c.phones && c.phones.length > 0 ? c.phones : [""],
        emails: c.emails && c.emails.length > 0 ? c.emails : [""]
      }));
    }
    let bank_accounts = v.bank_accounts;
    if (!bank_accounts || bank_accounts.length === 0) {
      bank_accounts = [{ account_name: v.bank_account_name || "", account_number: v.bank_account_number || "", ifsc: v.bank_ifsc || "", bank_name: v.bank_name || "", branch: v.bank_branch || "", is_primary: true }];
    }
    setFormData({ ...v, credit_limit: v.credit_limit_paise ? (v.credit_limit_paise / 100) : 0, contacts, bank_accounts, supplied_item_categories: v.supplied_item_categories || [], supplied_item_names: v.supplied_item_names || [] });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        const editable = ["vendor_name","vendor_type","brand","gstin","pan","address_line1","address_line2","nearest_landmark","city","state","pincode","country","contacts","supplied_item_categories","supplied_item_names","payment_terms_days","bank_accounts","pm_logistics_borne_by","drop_ship_address","primary_coordinator_user_id","backup_coordinator_user_id","is_backup_active","linked_sku_ids","notes","primary_contact_name","primary_contact_phone","primary_contact_email","secondary_contact_name","secondary_contact_phone","credit_limit"];
        const payload = {}; editable.forEach(k => { if (formData[k] !== undefined) payload[k] = formData[k]; });
        const { data } = await api.put(`/ops/inventory/vendors/${editId}`, payload);
        toast.success(data?.has_pending_changes ? "Changes submitted for Admin approval" : "Vendor updated");
      } else {
        await api.post("/ops/inventory/vendors", formData);
        toast.success("Vendor created as draft");
      }
      setShowForm(false);
      fetchVendors();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/ops/inventory/vendors/${id}/approve`);
      toast.success("Vendor approved & active");
      fetchVendors(); setViewVendor(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // GSTIN auto-state
  const handleGstinChange = (val) => {
    const g = val.toUpperCase();
    setFormData(p => {
      const upd = { ...p, gstin: g };
      if (g.length >= 2) {
        const st = GST_STATE_CODES[g.slice(0, 2)];
        if (st && !p.state) upd.state = st;
      }
      return upd;
    });
  };

  // Contacts helpers
  const updateContact = (idx, field, val) => {
    setFormData(p => { const c = [...p.contacts]; c[idx] = { ...c[idx], [field]: val }; return { ...p, contacts: c }; });
  };
  const addContact = () => { if (formData.contacts.length >= 5) return; setFormData(p => ({ ...p, contacts: [...p.contacts, { ...EMPTY_CONTACT }] })); };
  const removeContact = (idx) => { setFormData(p => ({ ...p, contacts: p.contacts.filter((_, i) => i !== idx) })); };
  const addPhone = (ci) => { setFormData(p => { const c = [...p.contacts]; c[ci] = { ...c[ci], phones: [...c[ci].phones, ""] }; return { ...p, contacts: c }; }); };
  const removePhone = (ci, pi) => { setFormData(p => { const c = [...p.contacts]; c[ci] = { ...c[ci], phones: c[ci].phones.filter((_, i) => i !== pi) }; return { ...p, contacts: c }; }); };
  const updatePhone = (ci, pi, val) => { setFormData(p => { const c = [...p.contacts]; const ph = [...c[ci].phones]; ph[pi] = val; c[ci] = { ...c[ci], phones: ph }; return { ...p, contacts: c }; }); };
  const addEmail = (ci) => { setFormData(p => { const c = [...p.contacts]; c[ci] = { ...c[ci], emails: [...c[ci].emails, ""] }; return { ...p, contacts: c }; }); };
  const removeEmail = (ci, ei) => { setFormData(p => { const c = [...p.contacts]; c[ci] = { ...c[ci], emails: c[ci].emails.filter((_, i) => i !== ei) }; return { ...p, contacts: c }; }); };
  const updateEmail = (ci, ei, val) => { setFormData(p => { const c = [...p.contacts]; const em = [...c[ci].emails]; em[ei] = val; c[ci] = { ...c[ci], emails: em }; return { ...p, contacts: c }; }); };

  // Bank helpers
  const updateBank = (idx, field, val) => { setFormData(p => { const b = [...p.bank_accounts]; b[idx] = { ...b[idx], [field]: val }; return { ...p, bank_accounts: b }; }); };
  const addBank = () => { setFormData(p => ({ ...p, bank_accounts: [...p.bank_accounts, { ...EMPTY_BANK, is_primary: false }] })); };
  const removeBank = (idx) => { setFormData(p => ({ ...p, bank_accounts: p.bank_accounts.filter((_, i) => i !== idx) })); };
  const setPrimaryBank = (idx) => { setFormData(p => ({ ...p, bank_accounts: p.bank_accounts.map((b, i) => ({ ...b, is_primary: i === idx })) })); };

  // Item category toggles
  const toggleCategory = (cat) => {
    setFormData(p => {
      const cats = p.supplied_item_categories || [];
      return { ...p, supplied_item_categories: cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat] };
    });
  };

  const is3P = ["3p_hybrid_manufacturer", "3p_buyout_manufacturer"].includes(formData.vendor_type);

  return (
    <div className="space-y-4" data-testid="ops-vendors-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="ops-vendors-title">Vendor Master</h1>
        <div className="flex gap-2">
          {admin && <Button variant="outline" onClick={() => { const h = ["Vendor Code","Vendor Name","Vendor Type","GSTIN","State","Status","Primary Contact","Primary Phone","Payment Terms (days)","Items Supplied","Created Date"]; const rows = vendors.map(v => [v.vendor_code||"",v.vendor_name||"",(v.vendor_type||"").replace(/_/g," "),v.gstin||"",v.state||"",v.status||"",(v.contacts&&v.contacts[0]?.name)||"",(v.contacts&&v.contacts[0]?.phones?.[0])||"",v.payment_terms_days||"",(v.supplied_item_names||[]).join("; "),(v.created_at||"").slice(0,10)]); const csv = [h,...rows].map(r => r.map(val => `"${String(val||"").replace(/"/g,'""')}"`).join(",")).join("\n"); const bl = new Blob([csv],{type:"text/csv"}); const a = document.createElement("a"); a.href = URL.createObjectURL(bl); a.download = `vendor_master_${istToday()}.csv`; a.click(); }} data-testid="export-vendors-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>}
          <Button onClick={openCreate} data-testid="create-vendor-btn"><Plus className="w-4 h-4 mr-1" />New Vendor</Button>
        </div>
      </div>

      {admin && pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between" data-testid="vendors-pending-banner">
          <span className="text-sm text-amber-800 font-medium">{pendingCount} vendor(s) awaiting your approval</span>
          <Button size="sm" variant="outline" onClick={() => setFilterStatus("draft")} className="text-amber-800 border-amber-300 hover:bg-amber-100">Review Now</Button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end" data-testid="vendors-filter-bar">
        <div className="w-64 relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
          <Input placeholder="Search code or name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="vendors-search" />
        </div>
        <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="vendors-filter-type"><SelectValue placeholder="All Vendor Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Vendor Types</SelectItem>{vendorTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="vendors-filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Search by supplied item..." value={filterSupplied} onChange={e => setFilterSupplied(e.target.value)} className="w-48" data-testid="vendors-filter-supplied" />
      </div>

      <div className="border rounded-lg overflow-auto" data-testid="vendors-table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-28">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-44">Type</TableHead>
              <TableHead className="w-28">City</TableHead>
              <TableHead className="w-36">GSTIN</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">No vendors found</TableCell></TableRow>
            ) : vendors.map(v => (
              <React.Fragment key={v._id}>
              <TableRow data-testid={`vendor-row-${v.vendor_code}`}>
                <TableCell className="font-mono text-xs">{v.vendor_code}</TableCell>
                <TableCell className="font-medium">{v.vendor_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{(v.vendor_type || "").replace(/_/g, " ")}</Badge></TableCell>
                <TableCell>{v.city}</TableCell>
                <TableCell className="text-xs">{v.gstin || "—"}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[v.status] || "bg-zinc-100"}`}>{v.status}</span></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" onClick={() => setViewVendor(v)} data-testid={`view-vendor-${v.vendor_code}`}><Eye className="w-4 h-4" /></Button>
                  {(v.status === "draft" || admin || canDraft) && <Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.vendor_code}`}><Pencil className="w-4 h-4" /></Button>}
                  {v.has_pending_changes && <span className="px-1.5 py-0.5 text-[8px] font-semibold bg-orange-100 text-orange-700 rounded-sm ml-1">Pending Approval</span>}
                  {admin && v.has_pending_changes && (<div className="inline-flex items-center gap-1 ml-1">
                    <Button size="sm" variant="outline" className="h-6 text-[9px] border-blue-300 text-blue-700" onClick={async () => {
                      if (expandedDiff[v._id]) { setExpandedDiff(p => { const n = {...p}; delete n[v._id]; return n; }); return; }
                      let vc = v; if (!v.pending_changes) { try { const { data } = await api.get(`/ops/inventory/vendors/${v._id}`); vc = data; } catch { /* ignore */ } }
                      setExpandedDiff(p => ({...p, [v._id]: { pending: vc.pending_changes || {}, current: vc }}));
                    }}>{expandedDiff[v._id] ? "Hide" : "View"} Changes</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[9px] border-green-300 text-green-700" onClick={async () => { try { await api.put(`/ops/inventory/vendors/${v._id}/pending/approve`); toast.success("Changes approved"); setExpandedDiff(p => { const n = {...p}; delete n[v._id]; return n; }); fetchVendors(); } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>Approve</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[9px] border-red-300 text-red-700" onClick={async () => { const reason = prompt("Rejection reason (optional):"); try { await api.put(`/ops/inventory/vendors/${v._id}/pending/reject`, { reason: reason || "" }); toast.success("Changes rejected"); setExpandedDiff(p => { const n = {...p}; delete n[v._id]; return n; }); fetchVendors(); } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>Reject</Button>
                  </div>)}
                </TableCell>
              </TableRow>
              {expandedDiff[v._id] && (() => { const d = expandedDiff[v._id]; const LABELS = { vendor_name: "Name", vendor_type: "Type", brand: "Brand", gstin: "GSTIN", pan: "PAN", address_line1: "Address 1", address_line2: "Address 2", city: "City", state: "State", pincode: "Pincode", contacts: "Contacts", supplied_item_categories: "Categories", supplied_item_names: "Supplied Items", payment_terms_days: "Payment Terms (days)", credit_limit_paise: "Credit Limit", bank_accounts: "Bank Accounts", pm_logistics_borne_by: "Freight Borne By", notes: "Notes", primary_contact_name: "Primary Contact", primary_contact_phone: "Primary Phone", primary_contact_email: "Primary Email" };
                const fmt = (val, key) => { if (val == null) return "\u2014"; if (Array.isArray(val)) { if (key === "contacts") return val.map(c => `${c.name || ""} / ${(c.phones||[])[0]||""} / ${(c.emails||[])[0]||""}`).join("; ") || "\u2014"; if (key === "bank_accounts") return val.map(b => `${b.account_name||""} \u2014 ${b.account_number||""}`).join("; ") || "\u2014"; return val.join(", ") || "\u2014"; } if (typeof val === "object") return JSON.stringify(val); if (key === "credit_limit_paise") return `Rs ${(val/100).toLocaleString("en-IN")}`; return String(val) || "\u2014"; };
                return <TableRow><TableCell colSpan={7} className="bg-amber-50 p-0"><div className="px-4 py-2"><table className="w-full text-xs"><thead><tr className="text-zinc-500"><th className="text-left py-1 font-medium w-1/5">Field</th><th className="text-left py-1 font-medium w-2/5">Current</th><th className="text-left py-1 font-medium w-2/5">Proposed</th></tr></thead><tbody>{Object.entries(d.pending).map(([k, nv]) => <tr key={k} className="border-t border-amber-100"><td className="py-1 font-medium text-zinc-600">{LABELS[k] || k.replace(/_/g, " ")}</td><td className="py-1 text-zinc-500">{fmt(d.current[k], k)}</td><td className="py-1 text-emerald-700 font-medium">{fmt(nv, k)}</td></tr>)}</tbody></table></div></TableCell></TableRow>; })()}
            </React.Fragment> ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-zinc-500" data-testid="vendors-count">Showing {vendors.length} vendor(s)</p>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="vendor-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Vendor" : "New Vendor (Draft)"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Basic Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Vendor Name *</Label><Input value={formData.vendor_name} onChange={e => setFormData(p => ({ ...p, vendor_name: e.target.value }))} data-testid="vendor-name-input" /></div>
              <div>
                <Label>Vendor Type *</Label>
                <Select value={formData.vendor_type} onValueChange={v => setFormData(p => ({ ...p, vendor_type: v }))}>
                  <SelectTrigger data-testid="vendor-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{vendorTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Brand</Label>
                <Select
                  value={brandLinkOn ? (formData.brand_id || "") : (formData.brand || "Skippi")}
                  onValueChange={v => setFormData(p => brandLinkOn
                    ? ({ ...p, brand_id: v, brand: (brandOptions.find(b => b.id === v) || {}).name || "" })
                    : ({ ...p, brand: v }))}
                >
                  <SelectTrigger data-testid="vendor-brand-select"><SelectValue placeholder={brandLinkOn ? "Choose an active brand" : undefined} /></SelectTrigger>
                  <SelectContent data-testid="vendor-brand-options">
                    {brandOptions.map(b => (
                      <SelectItem key={b.id || b.name} value={brandLinkOn ? b.id : b.name}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GSTIN *</Label>
                <Input value={formData.gstin} onChange={e => handleGstinChange(e.target.value)} maxLength={15} data-testid="vendor-gstin-input" />
                <p className="text-[10px] text-zinc-400 mt-1">15-char GST number. State auto-fills from first 2 digits.</p>
              </div>
              <div><Label>PAN</Label><Input value={formData.pan} onChange={e => setFormData(p => ({ ...p, pan: e.target.value.toUpperCase() }))} maxLength={10} /></div>
            </div>

            {/* 2E: Item Categories Supplied */}
            {formData.vendor_type && (
              <>
                <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Item Categories Supplied</h3>
                <div className="flex flex-wrap gap-3">
                  {itemCategories.map(cat => (
                    <label key={cat} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox checked={(formData.supplied_item_categories || []).includes(cat)} onCheckedChange={() => toggleCategory(cat)} />
                      {cat}
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">Select all categories this vendor supplies</p>
              </>
            )}
            {/* Supplied Items tags */}
            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Items Supplied</h3>
            <div>
              <p className="text-xs text-zinc-400 mb-1">Type item name and press Enter to add. Helps search vendors by what they supply.</p>
              <div className="flex flex-wrap gap-1 mb-2">{(formData.supplied_item_names || []).map((tag, i) => <span key={i} className="inline-flex items-center gap-1 bg-teal-50 border border-teal-200 text-teal-800 text-xs rounded-full px-2 py-0.5">{tag}<button type="button" onClick={() => setFormData(p => ({ ...p, supplied_item_names: (p.supplied_item_names || []).filter((_, j) => j !== i) }))} className="hover:text-red-600">x</button></span>)}</div>
              <Input placeholder="Type item name + Enter" className="h-8 text-xs" onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) { e.preventDefault(); setFormData(p => ({ ...p, supplied_item_names: [...(p.supplied_item_names || []), e.target.value.trim()] })); e.target.value = ""; } }} data-testid="vendor-supplied-input" />
            </div>

            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Address</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Address Line 1</Label><Input value={formData.address_line1} onChange={e => setFormData(p => ({ ...p, address_line1: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Address Line 2</Label><Input value={formData.address_line2} onChange={e => setFormData(p => ({ ...p, address_line2: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Nearest Landmark</Label><Input value={formData.nearest_landmark || ""} onChange={e => setFormData(p => ({ ...p, nearest_landmark: e.target.value }))} placeholder="e.g. Near Hitec City metro station" /></div>
              <div><Label>City</Label><Input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} /></div>
              <div>
                <Label>State</Label>
                <Select value={formData.state || "_none"} onValueChange={v => setFormData(p => ({ ...p, state: v === "_none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{indianStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Pincode</Label><Input value={formData.pincode} onChange={e => setFormData(p => ({ ...p, pincode: e.target.value }))} maxLength={6} /></div>
            </div>

            {/* 2C: Multiple Contacts */}
            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Contact Persons</h3>
            {(formData.contacts || []).map((c, ci) => (
              <div key={ci} className="border rounded p-3 space-y-2 bg-zinc-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-500">Contact #{ci + 1}</span>
                  {formData.contacts.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 h-6 text-xs" onClick={() => removeContact(ci)}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={c.role || "_none"} onValueChange={v => updateContact(ci, "role", v === "_none" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>{contactRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Name</Label><Input value={c.name} onChange={e => updateContact(ci, "name", e.target.value)} className="h-8 text-xs" /></div>
                </div>
                <div>
                  <Label className="text-xs">Phone(s)</Label>
                  {(c.phones || [""]).map((ph, pi) => (
                    <div key={pi} className="flex gap-1 mb-1">
                      <Input value={ph} onChange={e => updatePhone(ci, pi, e.target.value)} className="h-7 text-xs" placeholder="Phone" />
                      {(c.phones || []).length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removePhone(ci, pi)}><X className="w-3 h-3 text-red-400" /></Button>}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => addPhone(ci)}><Plus className="w-3 h-3 mr-0.5" />Phone</Button>
                </div>
                <div>
                  <Label className="text-xs">Email(s)</Label>
                  {(c.emails || [""]).map((em, ei) => (
                    <div key={ei} className="flex gap-1 mb-1">
                      <Input value={em} onChange={e => updateEmail(ci, ei, e.target.value)} className="h-7 text-xs" placeholder="Email" />
                      {(c.emails || []).length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeEmail(ci, ei)}><X className="w-3 h-3 text-red-400" /></Button>}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => addEmail(ci)}><Plus className="w-3 h-3 mr-0.5" />Email</Button>
                </div>
                <div><Label className="text-xs">WhatsApp</Label><Input value={c.whatsapp || ""} onChange={e => updateContact(ci, "whatsapp", e.target.value)} className="h-8 text-xs" placeholder="WhatsApp number" /></div>
              </div>
            ))}
            {(formData.contacts || []).length < 5 && <Button size="sm" variant="outline" onClick={addContact}><UserPlus className="w-3 h-3 mr-1" />Add Contact Person</Button>}

            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Financial</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Payment Terms (days)</Label><Input type="number" value={formData.payment_terms_days} onChange={e => setFormData(p => ({ ...p, payment_terms_days: parseInt(e.target.value) || 0 }))} /></div>
              <div><Label>Credit Limit (INR)</Label><Input type="number" value={formData.credit_limit} onChange={e => setFormData(p => ({ ...p, credit_limit: parseFloat(e.target.value) || 0 }))} data-testid="vendor-credit-limit" /></div>
            </div>

            {/* 2D: Multiple Bank Accounts */}
            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Bank Accounts</h3>
            {(formData.bank_accounts || []).map((b, bi) => (
              <div key={bi} className="border rounded p-3 space-y-2 bg-zinc-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-zinc-500">Bank #{bi + 1}</span>
                    {b.is_primary && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Primary</Badge>}
                    {!b.is_primary && <Button size="sm" variant="ghost" className="h-5 text-[10px] text-blue-600" onClick={() => setPrimaryBank(bi)}>Set Primary</Button>}
                  </div>
                  {formData.bank_accounts.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 h-6 text-xs" onClick={() => removeBank(bi)}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Account Name</Label><Input value={b.account_name} onChange={e => updateBank(bi, "account_name", e.target.value)} className="h-8 text-xs" /></div>
                  <div><Label className="text-xs">Account Number</Label><Input value={b.account_number} onChange={e => updateBank(bi, "account_number", e.target.value)} className="h-8 text-xs" data-testid="vendor-bank-account" /></div>
                  <div><Label className="text-xs">IFSC Code</Label><Input value={b.ifsc} onChange={e => updateBank(bi, "ifsc", e.target.value.toUpperCase())} className="h-8 text-xs" /></div>
                  <div><Label className="text-xs">Bank Name</Label><Input value={b.bank_name} onChange={e => updateBank(bi, "bank_name", e.target.value)} className="h-8 text-xs" /></div>
                  <div><Label className="text-xs">Branch</Label><Input value={b.branch} onChange={e => updateBank(bi, "branch", e.target.value)} className="h-8 text-xs" /></div>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addBank}><Plus className="w-3 h-3 mr-1" />Add Bank Account</Button>

            {is3P && (
              <>
                <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">3P Coordinator Assignment</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Primary Coordinator *</Label>
                    <Select value={formData.primary_coordinator_user_id || "_none"} onValueChange={v => setFormData(p => ({ ...p, primary_coordinator_user_id: v === "_none" ? "" : v }))}>
                      <SelectTrigger data-testid="vendor-primary-coord"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Backup Coordinator</Label>
                    <Select value={formData.backup_coordinator_user_id || "_none"} onValueChange={v => setFormData(p => ({ ...p, backup_coordinator_user_id: v === "_none" ? "" : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2"><Switch checked={formData.is_backup_active} onCheckedChange={v => setFormData(p => ({ ...p, is_backup_active: v }))} /><Label>Backup Active</Label></div>
                </div>
              </>
            )}

            <h3 className="text-sm font-semibold text-zinc-600 border-b pb-1">Logistics</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>PM Logistics Borne By</Label>
                <Select value={formData.pm_logistics_borne_by} onValueChange={v => setFormData(p => ({ ...p, pm_logistics_borne_by: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="company">Company</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea spellCheck={true} autoCorrect="on" value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-vendor-btn">{saving ? "Saving..." : editId ? "Update" : "Create Draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail */}
      <Dialog open={!!viewVendor} onOpenChange={() => setViewVendor(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="vendor-detail-dialog">
          <DialogHeader><DialogTitle>Vendor — {viewVendor?.vendor_code}</DialogTitle></DialogHeader>
          {viewVendor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Detail label="Name" value={viewVendor.vendor_name} />
                <Detail label="Type" value={(viewVendor.vendor_type || "").replace(/_/g, " ")} />
                <Detail label="Brand" value={viewVendor.brand} />
                <Detail label="GSTIN" value={viewVendor.gstin} />
                <Detail label="PAN" value={viewVendor.pan} />
                <Detail label="City" value={viewVendor.city} />
                <Detail label="State" value={viewVendor.state} />
                <Detail label="Landmark" value={viewVendor.nearest_landmark} />
                <Detail label="Payment Terms" value={`${viewVendor.payment_terms_days} days`} />
                <Detail label="Status" value={<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[viewVendor.status] || ""}`}>{viewVendor.status}</span>} />
                <Detail label="Created By" value={viewVendor.created_by_name} />
              </div>
              {(viewVendor.supplied_item_categories || []).length > 0 && (
                <Detail label="Supplied Categories" value={(viewVendor.supplied_item_categories || []).join(", ")} />
              )}
              {(viewVendor.contacts || []).length > 0 && (
                <div>
                  <span className="text-zinc-500 text-xs">Contacts</span>
                  {viewVendor.contacts.map((c, i) => (
                    <div key={i} className="text-xs border-b py-1">{c.role && <Badge variant="outline" className="text-[10px] mr-1">{c.role}</Badge>}{c.name} — {(c.phones || []).join(", ")} — {(c.emails || []).join(", ")}</div>
                  ))}
                </div>
              )}
              {viewVendor.status === "draft" && admin && (
                <div className="flex gap-2 pt-3 border-t">
                  <Button size="sm" onClick={() => handleApprove(viewVendor._id)} data-testid="approve-vendor-btn"><Check className="w-3 h-3 mr-1" />Approve</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (<div><span className="text-zinc-500 text-xs">{label}</span><div className="font-medium">{value || "—"}</div></div>);
}

function X({ className }) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
}
